import React, {useCallback, useEffect, useState} from 'react';
import Dialog from "../../../base/dialog";
import {Box, Card, CircularProgress, DialogActions, DialogContent, DialogTitle, Fade, Typography} from "@mui/material";
import Button from "../../../base/button/button";
import Api from "../../../../../core/services/api/api";
import useIsMounted from "../../../../hooks/use-is-mounted";
import TryAgain from "../../try-again";
import classnames from "classnames";
import {Switch} from "../../../base/switch";
import Grid2 from "@mui/material/Unstable_Grid2";

const WarehouseTaxManagementDialog = ({
                                          open,
                                          close,
                                          partNo,
                                          partId,
                                          warehouseCode,
                                      }) => {
    const [taxes, setTaxes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [updatingIds, setUpdatingIds] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Fetches the taxes of the part in the warehouse when the dialog is opened
     * @type {(function(): Promise<void>)|*}
     */
    const getTaxes = useCallback(async () => {
        setLoading(true);
        const response = await Api.loadPartTaxes(partId, warehouseCode);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTaxes(response.data ?? []);
        }
        setLoading(false);
    }, [isMounted, partId, warehouseCode])

    /**
     * Updates the schedule in the local state after a successful API update, without the need to refetch all the schedules
     */
    useEffect(() => {
        if (open) {
            void getTaxes();
        }
    }, [getTaxes, open]);

    /**
     * Changes operators subscription for a users' specific event.
     */
    const updateTaxEnablement = useCallback(async (tax, value) => {
        setTaxes(prevState => prevState.map(t => t.id === tax.id ? ({...t, enable: value}) : t));
        setUpdatingIds(prevState => [...prevState, tax.id]);
        const forApi = {
            partId: partId,
            warehouseCode: warehouseCode,
            taxList: taxes.map(t => ({
                taxCode: t.code,
                enable: t.id === tax.id ? value : t.enable,
            })),
        }
        const response = await Api.updatePartTax(forApi);
        if (!isMounted())
            return;
        setUpdatingIds(prevState => prevState.filter(id => id !== tax.id))
        if (!response?.resultFlag) {
            setTaxes(prevState => prevState.map(t => t.id === tax.id ? ({...t, enable: !value}) : t))
        }
    }, [isMounted, partId, warehouseCode, taxes]);


    return (
        <Dialog
            open={open}
            setOpen={close}
            maxWidth="sm"
            fullWidth
            className={'bound-dialog warehouse-tax-management-dialog'}
            slotProps={{root: {sx: {zIndex: theme => theme.zIndex.modal + 2}}}}
        >
            <DialogTitle sx={{display: 'flex', flexDirection: 'column', gap: 1}} component={'div'}>
                <Typography variant="h6" className={'p-3'}>
                    Manage Applicable Taxes of {partNo ?? ""} in {warehouseCode ?? ""}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: loading ? 2 : 0, py: 3, px: 3}}>
                    {
                        loading &&
                        <Grid2 container spacing={3}>
                            {
                                Array(4).fill(null).map((e, index) => (
                                    <Grid2 xs={12} key={index} className={'loading small'}>
                                        <div/>
                                    </Grid2>
                                ))
                            }
                        </Grid2>
                    }
                    {
                        !loading &&
                        taxes.length === 0 &&
                        <TryAgain
                            text={"There is currently no tax assigned to this part in this warehouse."}
                            buttonText={'Reload tax list'}
                            onClick={getTaxes}
                            showButton
                        />
                    }
                    {
                        !loading &&
                        taxes.length > 0 &&
                        taxes?.map((tax, index, taxes) => (
                            <Card
                                key={tax.id}
                                className={classnames('p-3 mx-4 mb-3 d-flex align-items-center' +
                                    ' justify-content-between', {
                                    'background-secondary-color-4': index % 2 === 0,
                                    'top-border-rounded': index === 0,
                                    'bottom-border-rounded': index + 1 === taxes.length,
                                })}
                            >
                                <p className={'text-sm font-weight-semi-bold black mr-4'}>
                                    {tax?.name ?? ''} | {tax.code ?? ""} ({tax?.rate ?? '--'}%)
                                </p>
                                <div className={'d-flex align-items-center justify-content-end'}>
                                    <Switch
                                        color={'primary'}
                                        checked={tax.enable ?? false}
                                        disabled={updatingIds.includes(tax.id)}
                                        onChange={(val) => updateTaxEnablement(tax, val)}
                                    />
                                    <Fade in={updatingIds.includes(tax.id)} className={'ml-3'}>
                                        <CircularProgress
                                            size={20}
                                            color={'secondary'}
                                        />
                                    </Fade>
                                </div>
                            </Card>
                        ))
                    }
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    disabled={!open}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={close}
                    borderRadius={'8px 0 8px 0'}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WarehouseTaxManagementDialog;
